import {useCallback, useContext, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {SeniorityExpectationsEditor} from './SeniorityExpectationsEditor';
import {TargetProfileDialogOverviewSection} from './TargetProfileDialogOverviewSection';
import {TargetProfileDialogSectionCard} from './TargetProfileDialogSectionCard';
import {TargetProfileDialogSkillsSection} from './TargetProfileDialogSkillsSection';
import EmpionDialog from '../../../../../components/EmpionDialog/EmpionDialog';
import {Loading} from '../../../../../components/Loading/Loading';
import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiRead} from '../../../../../hooks/useApiRead';
import {useApiWrite} from '../../../../../hooks/useApiWrite';
import {Delete} from '../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../lib/api/DELETE/DeleteRoutes';
import {GetRoutes} from '../../../../../lib/api/GET/GetRoutes';
import {Patch} from '../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../lib/api/PATCH/PatchRoutes';
import {Post} from '../../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../../lib/api/POST/PostRoutes';
import {DepartmentTargetProfileRequestBodies} from '../../../../../lib/api/RequestBodies/features/company/workforce/DepartmentTargetProfileRequestBodies';
import type {StandardSkill} from '../../../profile/types';
import {
  mapCompanyDepartmentTargetProfileDetailResponse,
  mapDepartmentTargetProfileDetailToDisplayProfile,
} from '../../serializers/DepartmentTargetProfileSerializers';
import {
  DepartmentTargetProfileDetail,
  SeniorityExpectations,
  SkillCategory,
  TargetProfileSkill,
} from '../../types/DepartmentTargetProfileTypes';

export interface Props {
  open: boolean;
  companyId: string;
  departmentId: string;
  assignmentId: string | null;
  onClose: () => void;
}

export const DepartmentTargetProfileViewDialog = ({open, companyId, departmentId, assignmentId, onClose}: Props) => {
  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation('translation', {keyPrefix: 'companySettings.departments.modal.targetProfiles'});
  const {t: tShared} = useTranslation();

  const {
    data: assignmentDetail,
    isLoading,
    isError,
    invalidateCache,
  } = useApiRead<DepartmentTargetProfileDetail | undefined>({
    path: GetRoutes.companyDepartmentTargetProfile(companyId, departmentId, assignmentId ?? ''),
    camelCaseOverride: mapCompanyDepartmentTargetProfileDetailResponse,
    options: {
      queryKey: ['companyDepartmentTargetProfile', companyId, departmentId, assignmentId],
      enabled: open && !!assignmentId,
    },
  });

  const displayProfile = useMemo(
    () => (assignmentDetail ? mapDepartmentTargetProfileDetailToDisplayProfile(assignmentDetail) : undefined),
    [assignmentDetail]
  );

  const onSkillMutationSuccess = useCallback(() => {
    invalidateCache();
    dispatchMessage(tShared('shared.formSubmit.success'), 'success');
  }, [dispatchMessage, invalidateCache, tShared]);

  const {
    makeRequest: updateExpectations,
    isLoading: isSaving,
    error: updateError,
  } = useApiWrite<
    Patch.UpdateDepartmentTargetProfile,
    DepartmentTargetProfileRequestBodies[Patch.UpdateDepartmentTargetProfile]
  >({
    method: 'patch',
    path: PatchRoutes[Patch.UpdateDepartmentTargetProfile](companyId, departmentId, assignmentId ?? ''),
    onSuccess: () => {
      invalidateCache();
      dispatchMessage(tShared('shared.formSubmit.success'), 'success');
    },
  });

  const {
    makeManualRequestWithParams: mutateSkill,
    isLoading: isMutatingSkill,
    error: skillMutationError,
  } = useApiWrite({
    disableLoadingOverlay: true,
    onSuccess: onSkillMutationSuccess,
  });

  const handleSaveExpectations = useCallback(
    (seniorityExpectations: SeniorityExpectations) => {
      if (!assignmentId) {
        return;
      }

      updateExpectations({
        companyDepartmentTargetProfile: {seniorityExpectations},
      });
    },
    [assignmentId, updateExpectations]
  );

  const handleRemoveSkill = useCallback(
    (standardSkillId: number) => {
      if (!assignmentId) {
        return;
      }

      mutateSkill({
        method: 'delete',
        path: DeleteRoutes[Delete.RemoveDepartmentTargetProfileSkill](
          companyId,
          departmentId,
          assignmentId,
          standardSkillId
        ),
      });
    },
    [assignmentId, companyId, departmentId, mutateSkill]
  );

  const handleAddSkill = useCallback(
    (skillCategory: SkillCategory, skill: StandardSkill, requirementLevel: 'must_have' | 'nice_to_have') => {
      if (!assignmentId) {
        return;
      }

      mutateSkill({
        method: 'post',
        path: PostRoutes[Post.AddDepartmentTargetProfileSkill](companyId, departmentId, assignmentId),
        body: {
          skill: {
            standardSkillResourceId: skill.resourceId,
            skillCategory,
            requirementLevel,
          },
        } satisfies DepartmentTargetProfileRequestBodies[Post.AddDepartmentTargetProfileSkill],
      });
    },
    [assignmentId, companyId, departmentId, mutateSkill]
  );

  const handleAddCustomSkill = useCallback(
    (skillCategory: SkillCategory, name: string, requirementLevel: 'must_have' | 'nice_to_have') => {
      if (!assignmentId) {
        return;
      }

      const trimmed = name.trim();
      if (!trimmed) {
        return;
      }

      mutateSkill({
        method: 'post',
        path: PostRoutes[Post.AddDepartmentTargetProfileSkill](companyId, departmentId, assignmentId),
        body: {
          skill: {
            name: trimmed,
            skillCategory,
            requirementLevel,
          },
        } satisfies DepartmentTargetProfileRequestBodies[Post.AddDepartmentTargetProfileSkill],
      });
    },
    [assignmentId, companyId, departmentId, mutateSkill]
  );

  const handleToggleRequirementLevel = useCallback(
    (skill: TargetProfileSkill) => {
      if (!assignmentId) {
        return;
      }

      const nextLevel = skill.requirementLevel === 'must_have' ? 'nice_to_have' : 'must_have';

      mutateSkill({
        method: 'patch',
        path: PatchRoutes[Patch.UpdateDepartmentTargetProfileSkill](
          companyId,
          departmentId,
          assignmentId,
          skill.standardSkillId
        ),
        body: {
          skill: {requirementLevel: nextLevel},
        } satisfies DepartmentTargetProfileRequestBodies[Patch.UpdateDepartmentTargetProfileSkill],
      });
    },
    [assignmentId, companyId, departmentId, mutateSkill]
  );

  useEffect(() => {
    if (isError) {
      dispatchMessage(tShared('shared.fetchingData.error'), 'error');
    }
  }, [dispatchMessage, isError, tShared]);

  useEffect(() => {
    if (updateError || skillMutationError) {
      dispatchMessage(tShared('shared.formSubmit.error'), 'error');
    }
  }, [dispatchMessage, skillMutationError, tShared, updateError]);

  return (
    <EmpionDialog open={open} onClose={onClose} title={t('viewDialog.title')} showCloseButton>
      {isLoading && (
        <Box sx={{py: 4}}>
          <Loading />
        </Box>
      )}
      {!isLoading && displayProfile && (
        <Box key={`${assignmentId}-${open ? 'open' : 'closed'}`}>
          <TargetProfileDialogOverviewSection profile={displayProfile} />
          <TargetProfileDialogSkillsSection
            profile={displayProfile}
            skillCurationDisabled={isMutatingSkill}
            onRemoveSkill={handleRemoveSkill}
            onAddSkill={handleAddSkill}
            onAddCustomSkill={handleAddCustomSkill}
            onToggleRequirementLevel={handleToggleRequirementLevel}
          />
          {assignmentDetail && (
            <TargetProfileDialogSectionCard
              title={String(t('seniorityExpectations.title'))}
              helper={String(t('seniorityExpectations.helper'))}
            >
              <SeniorityExpectationsEditor
                initialExpectations={assignmentDetail.seniorityExpectations ?? {}}
                isSaving={isSaving}
                onSave={handleSaveExpectations}
                hideTitle
              />
            </TargetProfileDialogSectionCard>
          )}
        </Box>
      )}
    </EmpionDialog>
  );
};
